'use client'

import { useState, useRef, useCallback, useEffect } from 'react'
import Calendar from './calendar'
import { EventClickArg, DateSelectArg } from '@fullcalendar/core'
import { EventFormModal } from './event-form-modal'
import { EventDetailsModal, EventDetails } from './event-details-modal'
import { EventSearch, EventSearchFilters } from './event-search'
import Button from '@/components/ui/button'
import { useAuth } from '@/context/auth-context'

interface CalendarContainerProps {
  className?: string
  meetupId?: string
  initialView?: string
  visibleDates?: Date[]
  calendarTitle?: string
  hideGlobalEvents?: boolean
  hideHeader?: boolean
  slotMinTime?: string
  slotMaxTime?: string
  location?: string
}

function CalendarContainer({
  className,
  meetupId,
  initialView,
  visibleDates,
  calendarTitle,
  hideGlobalEvents = false,
  hideHeader = false,
  slotMinTime,
  slotMaxTime,
  location,
}: CalendarContainerProps) {
  const { user } = useAuth()
  const [selectedEvent, setSelectedEvent] = useState<EventDetails | null>(null)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined)
  const [selectedEndDate, setSelectedEndDate] = useState<Date | undefined>(undefined)
  const [searchFilters, setSearchFilters] = useState<EventSearchFilters | null>(null)
  const calendarRef = useRef<{ refetchEvents: () => void; getApi: () => any }>(null)
  // Add a render counter to track component updates
  const renderCountRef = useRef(0)

  // Get the current user ID for event editing permissions
  const currentUserId = user?.id

  // Log key properties for debugging
  console.log(`[CALENDAR CONTAINER RENDER #${++renderCountRef.current}]`, {
    visibleDates: visibleDates ? visibleDates.map(d => d.toISOString()) : 'none',
    initialView,
    meetupId,
    hasCalendarRef: !!calendarRef.current,
  })

  // If we have visible dates, verify they are valid
  useEffect(() => {
    if (visibleDates && visibleDates.length > 0) {
      // Ensure all dates are valid Date objects
      const allValid = visibleDates.every(date => date instanceof Date && !isNaN(date.getTime()))
      if (!allValid) {
        console.error('[CALENDAR CONTAINER] Invalid dates in visibleDates array')
      } else {
        // Log the date range for debugging
        console.log(
          '[CALENDAR CONTAINER] Date range from',
          visibleDates[0].toISOString(),
          'to',
          visibleDates[visibleDates.length - 1].toISOString()
        )
      }
    }
  }, [visibleDates])

  // Track calendar API changes
  useEffect(() => {
    // Removing the interval that was checking and logging calendar state every 5 seconds
    // This was causing excessive console logging and potential performance issues
    /* 
    const interval = setInterval(() => {
      if (calendarRef.current?.getApi()) {
        const api = calendarRef.current.getApi()
        console.log('[CALENDAR CONTAINER] Calendar state check:', {
          view: api.view.type,
          title: api.view.title,
          currentStart: api.view.currentStart?.toISOString(),
          currentEnd: api.view.currentEnd?.toISOString(),
          renderId: renderCountRef.current,
        })
      }
    }, 5000) // Check every 5 seconds

    return () => clearInterval(interval)
    */
  }, [])

  // Add a direct DOM manipulation approach if needed
  useEffect(() => {
    if (!visibleDates || visibleDates.length === 0) return

    // Try forcing a specific date range on initial render and after any view changes
    const forceCorrectDateRange = () => {
      if (!calendarRef.current?.getApi) return

      try {
        const api = calendarRef.current.getApi()
        if (!api) return

        const firstDate = visibleDates[0]
        const lastDate = visibleDates[visibleDates.length - 1]

        console.log('[CALENDAR CONTAINER] Forcing correct date range:', {
          start: firstDate.toISOString(),
          end: lastDate.toISOString(),
          viewType: api.view.type,
          currentStart: api.view.currentStart?.toISOString(),
        })

        // 1. Apply validRange constraint (most restrictive)
        api.setOption('validRange', {
          start: firstDate,
          end: new Date(lastDate.getTime() + 86400000), // Add one day to include end date
        })

        // 2. Force the view to match our range
        api.gotoDate(firstDate)

        // 3. Set duration to match our exact range
        const dayCount =
          Math.round((lastDate.getTime() - firstDate.getTime()) / (24 * 60 * 60 * 1000)) + 1
        api.setOption('duration', { days: dayCount })

        // 4. Explicitly set visible range
        api.setOption('visibleRange', {
          start: firstDate,
          end: new Date(lastDate.getTime() + 86400000), // Add one day to include end date
        })

        // 5. Apply day restrictions if in week view
        if (api.view.type.includes('timeGrid')) {
          // Calculate days of week to show
          const visibleDaysOfWeek = visibleDates.map(date => date.getDay())
          const allDaysOfWeek = [0, 1, 2, 3, 4, 5, 6] // Sun-Sat
          const hiddenDays = allDaysOfWeek.filter(day => !visibleDaysOfWeek.includes(day))

          // Hide days of the week that aren't in our visible dates
          api.setOption('hiddenDays', hiddenDays)

          // Force first day to match our first date
          api.setOption('firstDay', firstDate.getDay())
        }

        // 6. Disable navigation buttons
        api.setOption('headerToolbar', {
          start: 'title',
          center: '',
          end: api.view.type, // Only show view type, remove nav buttons
        })

        // 7. Finally, update the title directly
        updateTitle()
      } catch (err) {
        console.error('[CALENDAR CONTAINER] Error forcing date range:', err)
      }
    }

    // Simple function to directly update the title
    const updateTitle = () => {
      try {
        const titleEl = document.querySelector('.fc-toolbar-title')
        if (titleEl) {
          const firstDate = visibleDates[0]
          const lastDate = visibleDates[visibleDates.length - 1]
          const startFormatted = firstDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          })
          const endFormatted = lastDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
          const newTitle = `${startFormatted} – ${endFormatted}`

          // Only update if it's not showing the correct date range
          if (
            !titleEl.textContent?.includes(startFormatted) ||
            !titleEl.textContent?.includes(endFormatted)
          ) {
            console.log(
              '[CALENDAR CONTAINER] Updating title from:',
              titleEl.textContent,
              'to:',
              newTitle
            )
            titleEl.textContent = newTitle
          }
        }
      } catch (e) {
        console.error('[CALENDAR CONTAINER] Error updating title:', e)
      }
    }

    // Apply immediately when component mounts
    setTimeout(forceCorrectDateRange, 0)

    // Also apply after a short delay to handle race conditions
    setTimeout(forceCorrectDateRange, 100)
    setTimeout(forceCorrectDateRange, 500)

    // Set up observers to maintain the correct date range

    // 1. Watch for calendar view changes
    const viewChangeObserver = new MutationObserver(() => {
      console.log('[CALENDAR CONTAINER] Calendar view changed, reapplying date range')
      forceCorrectDateRange()
    })

    // 2. Watch for title changes
    const titleObserver = new MutationObserver(() => {
      updateTitle()
    })

    // Wait for elements to be available
    setTimeout(() => {
      const viewEl = document.querySelector('.fc-view-harness')
      const titleEl = document.querySelector('.fc-toolbar-title')

      if (viewEl) {
        viewChangeObserver.observe(viewEl, { childList: true, subtree: true, attributes: true })
      } else {
        console.warn('[CALENDAR CONTAINER] Could not find calendar view element for observer')
      }

      if (titleEl) {
        titleObserver.observe(titleEl, { childList: true, characterData: true, subtree: true })
      } else {
        console.warn('[CALENDAR CONTAINER] Could not find title element for observer')
      }
    }, 200)

    // Cleanup
    return () => {
      viewChangeObserver.disconnect()
      titleObserver.disconnect()
    }
  }, [visibleDates])

  // Add enhanced logging at component mount to help diagnose Vercel issues
  useEffect(() => {
    console.log('[CALENDAR CONTAINER MOUNT] Initializing with properties:', {
      hasMeetupId: !!meetupId,
      initialView,
      visibleDatesCount: visibleDates?.length,
      browserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneDST:
        new Date().getTimezoneOffset() !==
        new Date(new Date().getFullYear(), 0, 1).getTimezoneOffset(),
      calendarTitle: calendarTitle || 'No title provided',
      userAgent: navigator.userAgent,
      windowInnerWidth: window.innerWidth,
      windowInnerHeight: window.innerHeight,
    })

    if (visibleDates && visibleDates.length > 0) {
      console.log(
        '[CALENDAR CONTAINER MOUNT] Visible dates:',
        visibleDates.map(d => ({
          date: d.toISOString(),
          localString: d.toString(),
          year: d.getFullYear(),
          month: d.getMonth() + 1,
          day: d.getDate(),
          dayOfWeek: d.getDay(),
        }))
      )
    }

    // Set up a one-time check to verify the calendar state after mounting
    const timeoutId = setTimeout(() => {
      if (calendarRef.current?.getApi) {
        const api = calendarRef.current.getApi()
        if (api) {
          console.log('[CALENDAR CONTAINER MOUNT] Calendar API state check:', {
            view: api.view.type,
            viewTitle: api.getCurrentData().viewTitle,
            currentStart: api.view.currentStart?.toISOString(),
            currentEnd: api.view.currentEnd?.toISOString(),
            activeStart: api.view.activeStart?.toISOString(),
            activeEnd: api.view.activeEnd?.toISOString(),
            calendarDate: api.getDate().toISOString(),
          })
        }
      }
    }, 1000)

    return () => clearTimeout(timeoutId)
  }, [meetupId, initialView, visibleDates, calendarTitle])

  // Handle event click to show details
  const handleEventClick = (clickInfo: EventClickArg) => {
    // Create a new object with the event data instead of modifying the original
    const eventData = {
      ...clickInfo.event.extendedProps,
      id: clickInfo.event.id,
      title: clickInfo.event.title,
      start: clickInfo.event.start ? clickInfo.event.start.toISOString() : '',
      end: clickInfo.event.end ? clickInfo.event.end.toISOString() : undefined,
    } as EventDetails

    setSelectedEvent(eventData)
    setIsDetailsModalOpen(true)
  }

  // Handle date selection
  const handleDateSelect = (selectInfo: DateSelectArg) => {
    // Prevent date selection outside visible dates
    if (visibleDates && visibleDates.length > 0) {
      const selectedDateMs = selectInfo.start.getTime()
      const firstVisibleDateMs = visibleDates[0].getTime()
      const lastVisibleDateMs = visibleDates[visibleDates.length - 1].getTime() + 86400000 // Add one day in ms

      // Check if selected date is within visible range
      if (selectedDateMs < firstVisibleDateMs || selectedDateMs > lastVisibleDateMs) {
        console.log('Date selection outside visible range, ignoring')
        return
      }
    }

    // Only store the start and end date if the user is authenticated
    if (user) {
      setSelectedDate(selectInfo.start)
      setSelectedEndDate(selectInfo.end)
      setIsCreateModalOpen(true)
    } else {
      // Show a message that the user needs to log in
      alert('Please log in to create an event')
    }
  }

  // Handle event creation completion
  const handleEventCreated = () => {
    // Close the modal
    setIsCreateModalOpen(false)
    setSelectedDate(undefined)
    setSelectedEndDate(undefined)

    // Refresh calendar events
    if (calendarRef.current) {
      calendarRef.current.refetchEvents()
    }
  }

  // Handle event deletion
  const handleEventDeleted = () => {
    // Close the details modal
    setIsDetailsModalOpen(false)
    setSelectedEvent(null)

    // Refresh calendar events
    if (calendarRef.current) {
      calendarRef.current.refetchEvents()
    }
  }

  // Handle search
  const handleSearch = (filters: EventSearchFilters) => {
    setSearchFilters(filters)
  }

  return (
    <div className={`calendar-container ${className || ''}`}>
      {!hideHeader && (
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
          <h2 className="text-xl font-bold mb-2 md:mb-0">{calendarTitle || 'Events Calendar'}</h2>
          <EventSearch onSearch={handleSearch} />
        </div>
      )}

      <div className="bg-background p-4 rounded-lg">
        <Calendar
          ref={calendarRef}
          className="calendar-widget"
          onEventClick={handleEventClick}
          onDateSelect={handleDateSelect}
          searchFilters={searchFilters}
          meetupId={meetupId}
          initialView={initialView || 'dayGridMonth'}
          visibleDates={visibleDates}
          hideGlobalEvents={hideGlobalEvents}
          slotMinTime={slotMinTime}
          slotMaxTime={slotMaxTime}
        />
      </div>

      {/* Event creation modal */}
      {isCreateModalOpen && selectedDate && (
        <EventFormModal
          isOpen={isCreateModalOpen}
          onClose={() => {
            setIsCreateModalOpen(false)
            setSelectedDate(undefined)
            setSelectedEndDate(undefined)
          }}
          onEventCreated={handleEventCreated}
          initialDate={selectedDate}
          initialEndDate={selectedEndDate}
          defaultMeetupId={meetupId}
        />
      )}

      {/* Event details modal */}
      {isDetailsModalOpen && selectedEvent && (
        <EventDetailsModal
          isOpen={isDetailsModalOpen}
          onClose={() => {
            setIsDetailsModalOpen(false)
            setSelectedEvent(null)
          }}
          event={selectedEvent}
          onEventDeleted={handleEventDeleted}
        />
      )}
    </div>
  )
}

export { CalendarContainer }
